<div
  class="meter-row"
  [class.drag-over]="isDragOver"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)"
  >
  <!-- Meter Header -->
  <div class="meter-header" (click)="expanded = !expanded">
    <div class="meter-info">
      <!-- Meter Drag Handle -->
      <div
        class="drag-handle"
        draggable="true"
        (dragstart)="onMeterDragStart($event)"
        (click)="$event.stopPropagation()"
        >
        <i class="material-symbols-rounded">drag_indicator</i>
      </div>
      <mat-checkbox
        [checked]="isMeterSelected"
        (change)="toggleMeterSelection()"
        (click)="$event.stopPropagation()"
      ></mat-checkbox>
      <button class="expand-btn" [class.collapsed]="!expanded">
        <i class="material-symbols-rounded">expand_more</i>
      </button>
      <div class="meter-identifier">
        <div class="meter-icon-wrapper">
          <i class="material-symbols-rounded meter-icon">speed</i>
        </div>
        <span class="meter-label">Meter:</span>
        @if (uanOptions.length > 1) {
          <div class="meter-account-select-wrapper" (click)="$event.stopPropagation()">
            <app-score-select
              [options]="uanOptions"
              [value]="primaryAccountValue"
              (valueChange)="onPrimaryAccountChange($event)">
            </app-score-select>
          </div>
        } @else if (uanOptions.length === 1) {
          <!-- Match the score-select trigger layout (value + badge chip) so
               single- and multi-option meters read consistently. -->
          <span class="meter-account-pill">
            <span class="meter-account-value">{{ uanOptions[0].value }}</span>
            @if (uanOptions[0].badge) {
              <span class="meter-account-badge">{{ uanOptions[0].badge }}</span>
            }
          </span>
        } @else {
          <span class="meter-account-value unknown">Unknown</span>
        }
        @if (secondaryLabels.length > 0) {
          <span class="secondary-ids">
            @for (label of secondaryLabels; track label; let isLast = $last) {
              <span class="secondary-id">{{ label }}</span>
              @if (!isLast) { <span class="id-separator">&middot;</span> }
            }
          </span>
        }
      </div>
      <app-match-status-badge [status]="'NEW'"></app-match-status-badge>
    </div>
    <div class="meter-stats">
      @if (meter.metrics.electric > 0) {
        <span class="stat commodity electric"><i class="material-symbols-rounded">bolt</i>Electric</span>
        <span class="stat usage">{{ meter.metrics.electric | number }} kWh</span>
      }
      @if (meter.metrics.gas > 0) {
        <span class="stat commodity gas"><i class="material-symbols-rounded">local_fire_department</i>Gas</span>
        <span class="stat usage">{{ meter.metrics.gas | number }} DTH</span>
      }
      <span class="stat"><i class="material-symbols-rounded">description</i> {{ meter.bills?.length || 0 }}</span>
    </div>
  </div>

  <!-- Bills Table -->
  <div class="bills-container" [class.expanded]="expanded">
    <div class="bills-content">
      <div class="bills-table-wrapper">
      <table class="bills-table">
        <thead>
          <!-- Column Headers -->
          <tr class="column-header-row">
            <th class="spacer-cell"></th>
            <th class="grip-cell"></th>
            <th class="checkbox-cell"></th>
            <th class="icon-cell"></th>
            <th class="warning-icon-cell"></th>

            @for (group of COLUMN_GROUPS; track group; let groupIndex = $index) {
              @for (key of group.columns; track key; let colIndex = $index) {
                @if (visibleColumns[key]) {
                  <th
                    app-sortable-header
                    [label]="key === 'actions' ? '' : (columnLabels[key] || '')"
                    [tooltip]="columnTooltips[key] || null"
                    [columnKey]="key"
                    [sortConfig]="sortConfig"
                    [align]="key === 'actions' ? 'right' : 'left'"
                    [class.actions-header-cell]="key === 'actions'"
                  ></th>
                }
              }
            }
          </tr>
        </thead>
        <tbody>
          @for (billGroup of billGroups; track billGroup; let groupIndex = $index; let isFirstGroup = $first) {
            <!-- Paired Bill Group -->
            @if (isPairedGroup(billGroup)) {
              <!-- Spacer/separator row before paired group -->
              <tr class="separator-row">
                <td [attr.colspan]="21"></td>
              </tr>
              <!-- Period Header for paired bills - clickable to open confirmation -->
              <tr class="group-header-row-body" [ngClass]="getGroupBgClass(billGroup.matchConfidence)" (click)="openPairConfirmation(billGroup)">
                <td class="accent-cell" [ngClass]="getGroupAccentClass(billGroup.matchConfidence, 'first')"></td>
                <td [attr.colspan]="20">
                  <div class="group-header-content">
                    <span [ngClass]="getMatchBadgeClass(billGroup.matchConfidence)">
                      <i class="material-symbols-rounded">{{ billGroup.rejected ? 'link_off' : 'link' }}</i>
                      {{ getMatchLabel(billGroup.matchConfidence) }}
                    </span>
                    <span class="period-text">{{ formatPeriod(billGroup.billingPeriod.start) }}</span>
                    <span class="click-hint">Click to review</span>
                  </div>
                </td>
              </tr>
            }
            <!-- Unpaired bills: simple divider -->
            @if (!isPairedGroup(billGroup) && !isFirstGroup) {
              <tr class="divider-row"><td [attr.colspan]="21"></td></tr>
            }
            <!-- Render bills from the group -->
            @for (bill of getBillsFromGroup(billGroup); track bill; let i = $index; let isLast = $last) {
              <tr
                [class.selected]="selectedBillIds.includes(bill.id)"
                [ngClass]="isPairedGroup(billGroup) ? getGroupBgClass(billGroup.matchConfidence) : ''"
                >
                <!-- Colored accent bar for paired bills -->
                <td class="accent-cell" [ngClass]="isPairedGroup(billGroup) ? getGroupAccentClass(billGroup.matchConfidence, isLast ? 'last' : 'middle') : ''">
                </td>
                @if (bill.status === BillStatus.ERROR) {
                  <!-- Error row: span all columns with error message -->
                  <td class="grip-cell"></td>
                  <td class="checkbox-cell" (click)="$event.stopPropagation()">
                    <mat-checkbox
                      [checked]="selectedBillIds.includes(bill.id)"
                      (change)="onToggleSelection(bill.id)"
                    ></mat-checkbox>
                  </td>
                  <td [attr.colspan]="99" class="error-row-cell">
                    <div class="error-row-content">
                      <i class="material-symbols-rounded error-row-icon">error</i>
                      <span class="error-row-message">{{ bill.error || 'Processing error' }}</span>
                      <span class="error-row-filename">{{ bill.file.name }}</span>
                      @if (billRetriable()[bill.id]) {
                        <button (click)="onRetryBill(bill); $event.stopPropagation()" class="status-badge error" title="Click to retry">
                          <i class="material-symbols-rounded">refresh</i>
                          Retry
                        </button>
                      }
                      <button (click)="billService.deleteBill(bill.id); $event.stopPropagation()" class="action-btn delete" title="Delete">
                        <i class="material-symbols-rounded">delete</i>
                      </button>
                    </div>
                  </td>
                } @else {
                <!-- Grip -->
                <td class="grip-cell">
                  <div
                    class="grip-icon"
                    draggable="true"
                    (dragstart)="onBillDragStart($event, bill.id)"
                    >
                    <i class="material-symbols-rounded">drag_indicator</i>
                  </div>
                </td>
                <!-- Checkbox -->
                <td class="checkbox-cell" (click)="$event.stopPropagation()">
                  <mat-checkbox
                    [checked]="selectedBillIds.includes(bill.id)"
                    (change)="onToggleSelection(bill.id)"
                  ></mat-checkbox>
                </td>
                <!-- Bill Icon (click to preview) -->
                <td class="icon-cell">
                  <button class="bill-icon-wrapper clickable" (click)="billService.previewBill.set(bill); $event.stopPropagation()" title="View bill">
                    <i class="material-symbols-rounded bill-row-icon">receipt</i>
                  </button>
                </td>
                <!-- Warning Icon -->
                <td class="warning-icon-cell">
                  @if (bill.warnings?.length) {
                    <i class="material-symbols-rounded row-warning-icon"
                      [matTooltip]="billWarningText[bill.id]"
                      matTooltipPosition="right"
                      matTooltipClass="bill-warning-tooltip">warning</i>
                  }
                </td>
                <!-- DATA COLUMNS -->
                @for (group of COLUMN_GROUPS; track group; let groupIndex = $index) {
                  @for (key of group.columns; track key; let colIndex = $index) {
                    @if (visibleColumns[key]) {
                      <td [class.actions-cell]="key === 'actions'">
                        @switch (key) {
                          @case ('status') {
                            <div class="status-container">
                              <!-- Paired icon for bills in a paired group -->
                              @if (isPairedGroup(billGroup)) {
                                <span class="paired-icon" title="{{ billGroup.rejected ? 'Unpaired from' : 'Paired with' }} {{ getBillType(bill.data) === 'utility' ? 'supplier' : 'utility' }} bill">
                                  <i class="material-symbols-rounded">{{ billGroup.rejected ? 'link_off' : 'link' }}</i>
                                </span>
                              }
                              @if (bill.status === BillStatus.PENDING) {
                                <span class="status-badge pending">
                                  Pending
                                </span>
                              }
                              @if (bill.status === BillStatus.PROCESSING) {
                                <span class="status-badge processing">
                                  <i class="material-symbols-rounded spinner">sync</i> Processing
                                </span>
                              }
                              @if (bill.status === BillStatus.REVIEW_NEEDED || bill.status === BillStatus.APPROVED) {
                                <span class="status-badge processed">
                                  <i class="material-symbols-rounded">check_circle</i> Processed
                                </span>
                              }
                              @if (bill.status === BillStatus.DUPLICATE_DETECTED) {
                                <button (click)="billService.openDuplicateResolver(bill); $event.stopPropagation()" class="status-badge duplicate">
                                  <i class="material-symbols-rounded">warning</i> Duplicate
                                </button>
                              }
                            </div>
                          }
                          @case ('type') {
                            <app-bill-type-badge [type]="getBillType(bill.data)" [commodity]="bill.commodityType ?? bill.data?.commodityType"></app-bill-type-badge>
                          }
                          @case ('provider') {
                            <div class="provider-name" title="Double-click to edit">{{ bill.data?.providerName || '-' }}</div>
                          }
                          @case ('utility') {
                            <div class="utility-name" [title]="bill.data?.utilityName || ''">{{ getUtilityDisplay(bill.data) }}</div>
                          }
                          @case ('legalEntity') {
                            <div class="entity-name" [title]="bill.data?.legalEntity || ''">{{ bill.data?.legalEntity || '-' }}</div>
                          }
                          @case ('fileName') {
                            <div class="file-name-cell" [title]="bill.file.name">
                              <i class="material-symbols-rounded">description</i>
                              <span class="file-name">{{ bill.file.name }}</span>
                            </div>
                          }
                          @case ('amount') {
                            <div class="currency-value">{{ bill.data?.totalAmount | currency:bill.data?.currency }}</div>
                          }
                          @case ('confidence') {
                            <div class="confidence-cell">
                              <i class="material-symbols-rounded icon-fill"
                                 [class.good]="(bill.data?.confidence || 0) > 0.75"
                                 [class.bad]="(bill.data?.confidence || 0) > 0.5 && (bill.data?.confidence || 0) <= 0.75"
                                 [class.ugly]="(bill.data?.confidence || 0) <= 0.5">
                                 fiber_manual_record
                              </i>
                              <span>{{ ((bill.data?.confidence || 0) * 100) | number:'1.0-0' }}%</span>
                            </div>
                          }
                          @case ('actions') {
                            <div class="action-buttons">
                              <button (click)="billService.previewBill.set(bill); $event.stopPropagation()" class="action-btn view" title="View bill">
                                <i class="material-symbols-rounded">description</i>
                              </button>
                              <button (click)="billService.deleteBill(bill.id); $event.stopPropagation()" class="action-btn delete" title="Delete">
                                <i class="material-symbols-rounded">delete</i>
                              </button>
                            </div>
                          }
                          @default {
                            <div class="data-value">{{ getBillDataValue(bill, key) }}</div>
                          }
                        }
                      </td>
                    }
                  }
                }
                }
              </tr>
              <!-- Spacer row after last bill in paired group -->
              @if (isLast && isPairedGroup(billGroup)) {
                <tr class="spacer-row"><td [attr.colspan]="21"></td></tr>
              }
            }
          }
        </tbody>
      </table>
      </div>
    </div>
  </div>
</div>